// App top bar: logo, the Meetings/Calls switch, live clock, and the account
// menu (history, admin, security pages, sign out). The switch is the app's
// primary navigation — the two things Brizina does — so it sits dead center.
// The clock ticks on a 30s interval in local state: rare, page-scoped, and
// outside the call path.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import logoUrl from '../assets/brizina-logo.svg';
import { api, Me } from '../api';
import { useCallsStore } from '../store/callsStore';
import { useChatStore } from '../store/chatStore';
import { pushToast } from '../store/callStore';
import { setSelfDnd, usePresenceStore } from '../store/presenceStore';
import { Avatar } from '../ui/Avatar';
import { Toggle } from '../ui/Toggle';
import { IconCalendar, IconCalls, IconCamera, IconChat, IconPeople } from '../ui/icons';
import { LinkDeviceModal } from './LinkDeviceModal';

function clockLabel(): string {
  const d = new Date();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const date = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  return `${time} - ${date}`;
}

interface Props {
  me: Me;
  /** Breadcrumb section shown after the logo (e.g. "Calendar"). */
  section?: string;
  onSignedOut: () => void;
}

export function TopBar({ me, section, onSignedOut }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Calls owns /calls and any live call route.
  const onCalls = pathname.startsWith('/calls') || pathname.startsWith('/call/');
  const onChat = pathname.startsWith('/chat');
  const onPeople = pathname.startsWith('/people');
  const [clock, setClock] = useState(clockLabel);
  const [menuOpen, setMenuOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setInterval(() => setClock(clockLabel()), 30_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const toggleMenu = useCallback(() => setMenuOpen((v) => !v), []);
  const signOut = useCallback(() => {
    void api.logout().then(onSignedOut);
  }, [onSignedOut]);
  const missed = useCallsStore((s) => s.missedUnseen);
  const chatUnread = useChatStore((s) => s.totalUnread);
  const goHistory = useCallback(() => navigate('/history'), [navigate]);
  const goAccount = useCallback(() => navigate('/account'), [navigate]);
  const goAdmin = useCallback(() => navigate('/admin'), [navigate]);
  const goSecurity = useCallback(() => navigate('/security'), [navigate]);
  const openLink = useCallback(() => {
    setMenuOpen(false);
    setLinkOpen(true);
  }, []);
  const closeLink = useCallback(() => setLinkOpen(false), []);

  return (
    <div className="bz-topbar">
      <div className="bz-topbar-left">
        <Link to="/">
          <img src={logoUrl} alt="Brizina" className="bz-logo" />
        </Link>
        {section && (
          <>
            <span className="bz-topbar-crumb">/</span>
            <span className="bz-topbar-section">{section}</span>
          </>
        )}
      </div>
      {me.callsEnabled && (
      <nav className="bz-appswitch" aria-label="Sections">
        <Link
          to="/"
          className={
            onCalls || onChat || onPeople
              ? 'bz-appswitch-item'
              : 'bz-appswitch-item bz-appswitch-item-on'
          }
          aria-current={onCalls || onChat || onPeople ? undefined : 'page'}
        >
          <IconCamera size={16} />
          Meetings
        </Link>
        <Link
          to="/people"
          className={onPeople ? 'bz-appswitch-item bz-appswitch-item-on' : 'bz-appswitch-item'}
          aria-current={onPeople ? 'page' : undefined}
        >
          <IconPeople size={16} />
          People
        </Link>
        <Link
          to="/calls"
          className={onCalls ? 'bz-appswitch-item bz-appswitch-item-on' : 'bz-appswitch-item'}
          aria-current={onCalls ? 'page' : undefined}
        >
          <IconCalls size={16} />
          Calls
          {missed > 0 && <span className="bz-calls-badge">{missed > 9 ? '9+' : missed}</span>}
        </Link>
        <Link
          to="/chat"
          className={onChat ? 'bz-appswitch-item bz-appswitch-item-on' : 'bz-appswitch-item'}
          aria-current={onChat ? 'page' : undefined}
        >
          <IconChat size={16} />
          Discussions
          {chatUnread > 0 && <span className="bz-calls-badge">{chatUnread > 9 ? '9+' : chatUnread}</span>}
        </Link>
      </nav>
      )}
      <div className="bz-topbar-right" ref={wrapRef}>
        <span className="bz-topbar-clock">{clock}</span>
        <Link to="/calendar" className="bz-iconbtn bz-iconbtn-flat" title="Calendar" aria-label="Calendar">
          <IconCalendar size={16} />
        </Link>
        <button
          type="button"
          className="bz-topbar-avatarbtn"
          onClick={toggleMenu}
          aria-label="Account menu"
        >
          <Avatar name={me.name} identity={me.id} isSelf size={34} photoUrl={me.avatarUrl} />
        </button>
        {menuOpen && (
          <div className="bz-menu bz-usermenu" role="menu">
            <DndToggle />
            <div className="bz-menu-sep" />
            <button type="button" className="bz-menu-item" onClick={goAccount}>
              Account settings
            </button>
            <button type="button" className="bz-menu-item" onClick={goHistory}>
              Meeting history
            </button>
            {me.isAdmin && (
              <button type="button" className="bz-menu-item" onClick={goAdmin}>
                Admin dashboard
              </button>
            )}
            <button type="button" className="bz-menu-item" onClick={goSecurity}>
              Security
            </button>
            <button type="button" className="bz-menu-item" onClick={openLink}>
              Link a device
            </button>
            <button type="button" className="bz-menu-item" onClick={signOut}>
              Sign out
            </button>
          </div>
        )}
      </div>
      {linkOpen && <LinkDeviceModal me={me} onClose={closeLink} />}
    </div>
  );
}

/** Do Not Disturb, where you actually reach for it: the account menu.
 *
 * Optimistic, because the toggle has to feel instant — but reverted if the
 * server refuses, since a switch that lies about whether you can be rung is
 * worse than one that is slow. */
function DndToggle() {
  const dnd = usePresenceStore((s) => s.dnd);
  const [busy, setBusy] = useState(false);

  const toggle = useCallback(() => {
    const next = !dnd;
    setBusy(true);
    setSelfDnd(next);
    api
      .setDnd(next)
      .then(() => pushToast(next ? 'Do not disturb is on — calls won’t ring' : 'Do not disturb is off'))
      .catch(() => {
        setSelfDnd(!next); // it never took: say so rather than lie
        pushToast('Could not change do not disturb', 'error');
      })
      .finally(() => setBusy(false));
  }, [dnd]);

  return (
    <div className="bz-menu-row">
      <span className="bz-menu-row-label">
        Do not disturb
        <span className="bz-menu-row-sub">Calls won’t ring on any device</span>
      </span>
      <Toggle on={dnd} onToggle={toggle} label="Do not disturb" disabled={busy} />
    </div>
  );
}
